import { useCallback, useMemo, useState } from "react"
import { Box, IconButton, MenuItem, Stack, Tooltip } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { generatePath, useNavigate } from "react-router-dom"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faFileArrowDown, faShop } from "@fortawesome/pro-light-svg-icons"
import { MRT_ColumnDef, MRT_FullScreenToggleButton, MRT_Row, MRT_RowSelectionState } from "material-react-table"

import {
    COMPANY_ADMIN_ORGANIZATION_USERS_CREATE_ROUTE,
    COMPANY_ADMIN_ORGANIZATION_USERS_EDIT_ROUTE,
    COMPANY_ADMIN_ROUTE,
} from "../../../../../../constants/routes"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { SuccessDisplay } from "../../../../../../core/api/errorTypes"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { useApiQueryTable } from "../../../../../../core/api/components/ApiQueryTable"
import { StandardButton } from "../../../../../../shared/components/buttons/StandardButton"
import {
    ImpersonationConfirmDialog,
    type ImpersonationTarget,
    toImpersonationTarget,
} from "../../../../../../shared/components/dialogs/ImpersonationConfirmDialog"
import { GROW_CELL_CLASS, useGridLayoutStyles } from "../../../../../../shared/components/table/useGridLayoutStyles"
import { StandardToggleGlobalFilterButton } from "../../../../../../shared/components/table/StandardToggleGlobalFilterButton"
import { DeleteConfirmDialog } from "../../../../../../shared/components/dialogs/DeleteConfirmDialog"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../../shared/components/links/StandardBreadcrumbs"
import { PageHeader } from "../../../../../../shared/components/pageHeader/PageHeader"
import { Page } from "../../../../../../shared/components/pageHeader/PageHeaderApi"
import { PillTab, StandardPillTabs } from "../../../../../../shared/components/tabs/StandardPillTabs"
import { COLUMN_SIZE } from "../../../../../../shared/constants/table/config"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { useCanImpersonate } from "../../../../../../shared/hooks/useCanImpersonate"
import { deleteUser } from "../../../../../users/userApi"
import { CompaniesUsersItem } from "../../../../../users/userTypes"
import { useCurrentCompany } from "../../../../hooks/useCurrentCompany"
import {
    CompanyAdminUserGroupsQuery,
    UserGroupsForCompanyResponse,
    fetchUserGroupsForCompany,
} from "../../userGroups/userGroupsApi"

import { CompaniesUsersResponse, CompanyAdminUsersQuery, fetchCompaniesUsers } from "../usersApi"
import { CompanyUserDetailsType } from "../companyUserDetailsTypes"
import { CompanyUserDetailsDrawer } from "../components/CompanyUserDetailsDrawer"
import { ALL_USER_GROUPS, SelectUserGroupDropdown } from "../components/SelectUserGroupDropdown"
import { ALL_WORKGROUPS, SelectWorkgroupDropdown } from "../components/SelectWorkgroupDropdown"
import { UserNameCell } from "../components/UserNameCell"
import { UsersBulkActionBar } from "../components/UsersBulkActionBar"
import { useExportUsers } from "../hooks/useExportUsers"
import { DestructiveMenuItem } from "../../../../../../shared/components/menus/DestructiveMenuItem"

enum StatusFilter {
    ALL = "all",
    ACTIVE = "active",
    INACTIVE = "inactive",
}

const useStyles = makeStyles()(() => ({
    toolbar: {
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: "1rem",
        padding: "1rem",
    },
    table: {
        ".MuiTableCell-root": {
            paddingLeft: "1rem",
            paddingRight: "1rem",
        },
        ".Mui-TableHeadCell-Content-Labels": {
            paddingLeft: 0,
        },
    },
    internalActions: {
        paddingBlock: ".5rem",
        paddingLeft: ".5rem",
    },
    exportIcon: {
        fontSize: "1.25rem",
    },
    inactiveRow: {
        opacity: 0.6,
    },
}))

const selectUserGroupOptions = (data: UserGroupsForCompanyResponse) =>
    data.userGroups.map((group) => ({ id: group.id, name: group.name }))

export function Users() {
    const { classes } = useStyles()
    const { classes: gridClasses } = useGridLayoutStyles()
    const { l10n } = useAppLocalization()
    const navigate = useNavigate()
    const { companyId } = useCurrentCompany()

    const [userGroupId, setUserGroupId] = useState<number>(ALL_USER_GROUPS)
    const [workgroupId, setWorkgroupId] = useState<number>(ALL_WORKGROUPS)
    const [statusFilter, setStatusFilter] = useState<StatusFilter>(StatusFilter.ALL)
    const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({})
    const [detailsUser, setDetailsUser] = useState<CompaniesUsersItem | null>(null)
    const [deleteTarget, setDeleteTarget] = useState<CompaniesUsersItem | null>(null)
    const [impersonationTarget, setImpersonationTarget] = useState<ImpersonationTarget | null>(null)
    const canImpersonate = useCanImpersonate()

    const selectUsers = useCallback(
        (data: CompaniesUsersResponse) => {
            if (statusFilter === StatusFilter.ACTIVE) return data.users.filter((user) => user.isActive)
            if (statusFilter === StatusFilter.INACTIVE) return data.users.filter((user) => !user.isActive)
            return data.users
        },
        [statusFilter],
    )

    const companyUsers = useApiQuery(fetchCompaniesUsers, {
        queryName: CompanyAdminUsersQuery.USERS_FOR_COMPANY,
        dependencies: {
            companyId,
            userGroupId: userGroupId === ALL_USER_GROUPS ? undefined : userGroupId,
            workgroupId: workgroupId === ALL_WORKGROUPS ? undefined : workgroupId,
        },
        select: selectUsers,
    })

    const userGroupsResult = useApiQuery(fetchUserGroupsForCompany, {
        queryName: CompanyAdminUserGroupsQuery.LIST,
        dependencies: { companyId },
        select: selectUserGroupOptions,
    })
    const userGroups = userGroupsResult.data ?? []

    const exportUsers = useExportUsers({ data: companyUsers.data })

    const { ApiQueryTable, columnFactory } = useApiQueryTable(companyUsers)

    const selectedUsers = useMemo(
        () => (companyUsers.data ?? []).filter((user) => rowSelection[String(user.id)]),
        [companyUsers.data, rowSelection],
    )

    const { mutateAsync: removeUser } = useApiMutation(deleteUser, {
        successDisplay: SuccessDisplay.SNACKBAR,
        async onSuccess(_params, _data, context) {
            await context.queryClient.invalidateQueries({ queryKey: [CompanyAdminUsersQuery.USERS_FOR_COMPANY] })
        },
    })

    const columns = useMemo<MRT_ColumnDef<CompaniesUsersItem>[]>(
        () => [
            columnFactory.nameColumn({
                header: l10n.getString("company-organization-users-table-header-name"),
                accessorKey: "displayName",
                size: COLUMN_SIZE.L,
                minSize: COLUMN_SIZE.L,
                maxSize: COLUMN_SIZE.XXXL,
                Cell: ({ row }) => <UserNameCell user={row.original} />,
                muiTableHeadCellProps: { className: GROW_CELL_CLASS },
                muiTableBodyCellProps: { className: GROW_CELL_CLASS },
            }),
            {
                header: l10n.getString("company-organization-users-table-header-email"),
                accessorKey: "email",
                size: COLUMN_SIZE.M,
                minSize: COLUMN_SIZE.M,
                maxSize: COLUMN_SIZE.M,
            },
            {
                header: l10n.getString("company-organization-users-table-header-user-group"),
                accessorKey: "userGroup.name",
                size: COLUMN_SIZE.M,
                minSize: COLUMN_SIZE.M,
                maxSize: COLUMN_SIZE.M,
            },
            {
                ...columnFactory.activeInactiveStatusColumn(
                    {
                        header: l10n.getString("company-organization-users-table-header-status"),
                        accessorKey: "isActive",
                    },
                    (row) => row.isActive,
                ),
                size: COLUMN_SIZE.S,
                minSize: COLUMN_SIZE.S,
                maxSize: COLUMN_SIZE.S,
            },
        ],
        [l10n, columnFactory],
    )

    const renderRowActionMenuItems = useCallback(
        ({ row, closeMenu }: { row: MRT_Row<CompaniesUsersItem>; closeMenu: () => void }) => [
            <MenuItem
                key={`details-${row.original.id}`}
                onClick={() => {
                    setDetailsUser(row.original)
                    closeMenu()
                }}
            >
                <LocalizedStrict id="company-organization-users-table-button-details">
                    <span>Details</span>
                </LocalizedStrict>
            </MenuItem>,
            <MenuItem
                key={`edit-${row.original.id}`}
                onClick={() => {
                    navigate(
                        generatePath(COMPANY_ADMIN_ORGANIZATION_USERS_EDIT_ROUTE, { userId: String(row.original.id) }),
                    )
                    closeMenu()
                }}
            >
                <LocalizedStrict id="company-organization-users-table-button-edit">
                    <span>Edit</span>
                </LocalizedStrict>
            </MenuItem>,
            canImpersonate && (
                <MenuItem
                    key={`impersonate-${row.original.id}`}
                    onClick={() => {
                        setImpersonationTarget(toImpersonationTarget(row.original))
                        closeMenu()
                    }}
                >
                    <LocalizedStrict id="company-organization-users-table-button-impersonate">
                        <span>Log in as</span>
                    </LocalizedStrict>
                </MenuItem>
            ),
            <DestructiveMenuItem
                key={`delete-${row.original.id}`}
                onClick={() => {
                    setDeleteTarget(row.original)
                    closeMenu()
                }}
            >
                <LocalizedStrict id="company-organization-users-table-button-delete">
                    <span>Delete</span>
                </LocalizedStrict>
            </DestructiveMenuItem>,
        ],
        [navigate, canImpersonate],
    )

    const statusFilterTabs = useMemo<PillTab<StatusFilter>[]>(
        () => [
            { value: StatusFilter.ALL, label: l10n.getString("company-users-status-filter-all") },
            { value: StatusFilter.ACTIVE, label: l10n.getString("company-users-status-filter-active") },
            { value: StatusFilter.INACTIVE, label: l10n.getString("company-users-status-filter-inactive") },
        ],
        [l10n],
    )

    return (
        <>
            <LocalizedStrict id="company-organization-users-page-header" attrs={{ title: true }}>
                <PageHeader
                    page={Page.COMPANY_USERS}
                    title="Users"
                    icon={<FontAwesomeIcon icon={faShop} />}
                    rightElement={
                        <LocalizedStrict id="company-organization-users-button-create">
                            <StandardButton onClick={() => navigate(COMPANY_ADMIN_ORGANIZATION_USERS_CREATE_ROUTE)}>
                                Create
                            </StandardButton>
                        </LocalizedStrict>
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="company-nav-item-home">
                            <BreadcrumbLink to={COMPANY_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="company-admin-nav-item-organization">
                            <Breadcrumb>Organization</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="company-admin-nav-item-users">
                            <Breadcrumb>Users</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            <Box className={classes.table}>
                <ApiQueryTable
                    columns={columns}
                    layoutMode="grid"
                    muiTablePaperProps={{ className: gridClasses.pinned }}
                    initialState={{ density: "spacious" }}
                    enableRowSelection
                    getRowId={(user) => String(user.id)}
                    state={{ rowSelection }}
                    onRowSelectionChange={setRowSelection}
                    muiTableBodyRowProps={({ row }) => ({
                        className: row.original.isActive ? undefined : classes.inactiveRow,
                    })}
                    renderRowActionMenuItems={renderRowActionMenuItems}
                    renderToolbarInternalActions={({ table }) => (
                        <Stack direction="row" spacing={1} className={classes.internalActions}>
                            <LocalizedStrict id="company-organization-users-tooltip-export" attrs={{ title: true }}>
                                <Tooltip title="Export">
                                    <span>
                                        <IconButton onClick={exportUsers} disabled={!companyUsers.data?.length}>
                                            <FontAwesomeIcon icon={faFileArrowDown} className={classes.exportIcon} />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </LocalizedStrict>
                            <StandardToggleGlobalFilterButton table={table} />
                            <MRT_FullScreenToggleButton table={table} />
                        </Stack>
                    )}
                    renderTopToolbarCustomActions={() => (
                        <Box className={classes.toolbar}>
                            <SelectUserGroupDropdown userGroupId={userGroupId} setUserGroupId={setUserGroupId} />
                            <SelectWorkgroupDropdown workgroupId={workgroupId} setWorkgroupId={setWorkgroupId} />
                            <StandardPillTabs tabs={statusFilterTabs} value={statusFilter} onChange={setStatusFilter} />
                        </Box>
                    )}
                />
            </Box>

            <CompanyUserDetailsDrawer
                userId={detailsUser?.id ?? 0}
                detailsType={CompanyUserDetailsType.USER}
                isActive={detailsUser?.isActive ?? false}
                open={Boolean(detailsUser)}
                onClose={() => setDetailsUser(null)}
                onEdit={() => {
                    if (!detailsUser) return
                    navigate(
                        generatePath(COMPANY_ADMIN_ORGANIZATION_USERS_EDIT_ROUTE, { userId: String(detailsUser.id) }),
                    )
                    setDetailsUser(null)
                }}
                onImpersonate={
                    canImpersonate && detailsUser
                        ? () => setImpersonationTarget(toImpersonationTarget(detailsUser))
                        : undefined
                }
            />

            <ImpersonationConfirmDialog target={impersonationTarget} onClose={() => setImpersonationTarget(null)} />

            <DeleteConfirmDialog
                open={Boolean(deleteTarget)}
                onClose={() => setDeleteTarget(null)}
                typeOfEntity={l10n.getString("company-users-delete-entity")}
                entityName={deleteTarget?.displayName ?? ""}
                handleClick={async () => {
                    if (!deleteTarget) return
                    await removeUser({ userId: deleteTarget.id })
                    setDeleteTarget(null)
                }}
            />

            {selectedUsers.length > 0 && (
                <UsersBulkActionBar
                    selectedUsers={selectedUsers}
                    userGroups={userGroups}
                    onClose={() => setRowSelection({})}
                />
            )}
        </>
    )
}
